import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, CuneiformMotif, StarOfIshtar } from "../Ornaments";
import { WingedBird } from "../MythicMotifs";

const FERN_TEXTURE = "/media/fern-texture.jpg";

export const Story = () => {
  const r1 = useReveal();
  const r2 = useReveal();
  const r3 = useReveal();
  const r4 = useReveal();
  return (
    <section id="story" className="section" data-testid="story-section" style={{ position: "relative", overflow: "hidden" }}>
      {/* Fern / ecological backdrop — Phillip's "tend the body like a garden" thread */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url('${FERN_TEXTURE}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.10,
          mixBlendMode: "luminosity",
          filter: "hue-rotate(-10deg) saturate(0.6) brightness(0.55)",
          pointerEvents: "none",
        }}
        data-testid="story-fern-texture"
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, #0a0806 0%, rgba(30,46,33,0.3) 50%, #0a0806 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Decorative drifting bird at distance */}
      <div style={{ position: "absolute", top: "12%", right: "6%", pointerEvents: "none" }} aria-hidden="true" className="pulse-glow">
        <WingedBird size={140} opacity={0.10} flipped />
      </div>
      {/* Faint star at the deep right */}
      <div style={{ position: "absolute", bottom: "10%", right: "-80px", pointerEvents: "none", opacity: 0.08 }} aria-hidden="true">
        <StarOfIshtar size={280} opacity={1} />
      </div>
      <div className="section-inner">
        <div ref={r1.ref} className={`reveal ${r1.inView ? "in-view" : ""}`} style={{ maxWidth: 760 }}>
          <Ornament />
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            Since the First Cities
          </p>
          <h2 className="section-title" data-testid="story-title">
            The Body Has Always <br /> Been the Record
          </h2>
          <CuneiformMotif width={200} />
        </div>

        <div style={{ marginTop: "3rem", display: "grid", gridTemplateColumns: "1fr", gap: "2.5rem", maxWidth: 880 }}>
          <p ref={r2.ref} className={`lead reveal ${r2.inView ? "in-view" : ""}`} data-testid="story-paragraph-1">
            The Sumerians — the first civilization to write things down — understood that the
            inner grid and the outer grid run on the same laws. The <em style={{ color: "var(--gold-bright)" }}>me</em>,
            the divine organizing principles, governed the body and the cosmos by the same
            frequency. Coherence was not optional. It was the architecture of a functioning life.
          </p>
          <p ref={r3.ref} className={`lead lead-muted reveal ${r3.inView ? "in-view" : ""}`} data-testid="story-paragraph-2">
            Somewhere in the long arc from ancient knowing to modern institution, the body became a
            problem to be solved rather than a system to be mapped and cultivated. Therapy
            addressed the mind. Fitness addressed the muscle. Neither taught you how to read
            your own grid — or how to consciously rewrite it.
          </p>
          <p ref={r4.ref} className={`lead reveal ${r4.inView ? "in-view" : ""}`} data-testid="story-paragraph-3">
            Somatic Equilibrium returns to the older understanding: that the body is a biological
            instrument capable of being known, mapped, and intentionally programmed toward
            coherence. <span style={{ color: "var(--gold-bright)" }}>Mentally. Physically. Energetically.</span>
            &nbsp;This is the greater human story, made practical.
          </p>
        </div>
      </div>
    </section>
  );
};
